/**
 * syncService.js
 *
 * Flushes the offline `syncQueue` Dexie table to the server by batching
 * all pending operations and posting them to POST /sync/batch.
 *
 * Retry logic:
 *   - Up to MAX_RETRIES attempts per operation.
 *   - After MAX_RETRIES failures the operation is marked `failed`.
 *
 * Sync triggers (registered at the bottom of this file):
 *   - `window.online` event
 *   - `document.visibilitychange` → visible while online
 *
 * The exported `getSyncStatus()` returns { pending, failed } counts so
 * the UI can display appropriate indicators.
 */

import db from "./offlineDb";
import { apiSlice } from "./apiSlice";
import { clearStoredOfflineContext, getOfflineContext, getLogoutBypass, setLogoutBypass, updateOfflineContextFromAuth } from "./offlineContext";
import { getApiBaseUrl } from "../helpers/apiUrl";

const BASE_URL = getApiBaseUrl();
const MAX_RETRIES = 3;
const BATCH_SIZE = 100; // max ops per HTTP call

// Avoid concurrent sync runs
let _syncing = false;

// Redux store reference – injected via `initSyncService`
let _store = null;

export function initSyncService(store) {
  _store = store;
  updateOfflineContextFromAuth(store?.getState?.());
}

function getToken() {
  try {
    const state = _store?.getState();
    if (state?.auth?.access_token) return state.auth.access_token;
    return JSON.parse(localStorage.getItem("access_token")) || null;
  } catch {
    return JSON.parse(localStorage.getItem("access_token") ?? "null") || null;
  }
}

/**
 * Returns the current sync queue status.
 * @returns {Promise<{ pending: number, failed: number }>}
 */
export async function getSyncStatus() {
  const [pending, failed] = await Promise.all([
    db.syncQueue.where("status").equals("pending").count(),
    db.syncQueue.where("status").equals("failed").count(),
  ]);
  return { pending, failed };
}

/**
 * Returns all failed sync queue items (for display in the SyncStatusBanner).
 * @returns {Promise<Array>}
 */
export async function getFailedItems() {
  return db.syncQueue.where("status").equals("failed").toArray();
}

/**
 * Removes a failed item from the queue (user dismissed / manually re-entered).
 * @param {number} id
 */
export async function dismissFailedItem(id) {
  await db.syncQueue.delete(id);
}

/**
 * Main sync function. Reads all pending ops, batches them and posts to /sync/batch.
 * Each operation result is processed individually (partial success is supported).
 */
export async function runSync() {
  if (_syncing || !navigator.onLine) return;

  const token = getToken();
  if (!token) return;

  _syncing = true;
  try {
    const pending = await db.syncQueue
      .where("status")
      .equals("pending")
      .sortBy("createdAt");

    if (pending.length === 0) return;

    // Process in batches
    for (let i = 0; i < pending.length; i += BATCH_SIZE) {
      const batch = pending.slice(i, i + BATCH_SIZE);

      const operations = batch.map((item) => ({
        type: item.entityType,
        action: item.action,
        payload: item.payload,
        client_timestamp: item.client_timestamp,
      }));

      let results;
      try {
        const resp = await fetch(`${BASE_URL}/sync/batch`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ operations }),
        });
        if (!resp.ok) {
          // Server-level failure — increment retries on all items in batch
          await _handleBatchFailure(batch);
          continue;
        }
        const data = await resp.json();
        results = data.results ?? [];
      } catch {
        // Network error — increment retries
        await _handleBatchFailure(batch);
        continue;
      }

      // Process per-operation results
      for (const result of results) {
        const item = batch[result.index];
        if (!item) continue;

        if (result.success) {
          if (result.skipped_as_stale) {
            await db.syncQueue.update(item.id, {
              syncOutcome: "server_newer",
              status: "failed",
              retries: MAX_RETRIES,
            });
            continue;
          }
          await db.syncQueue.delete(item.id);
        } else {
          const newRetries = (item.retries ?? 0) + 1;
          if (newRetries >= MAX_RETRIES) {
            await db.syncQueue.update(item.id, { status: "failed", retries: newRetries });
          } else {
            await db.syncQueue.update(item.id, { retries: newRetries });
          }
        }
      }

      // Invalidate relevant RTK Query cache tags after syncing
      _invalidateTags(batch);
    }
  } finally {
    _syncing = false;
  }
}

async function _handleBatchFailure(batch) {
  for (const item of batch) {
    const newRetries = (item.retries ?? 0) + 1;
    if (newRetries >= MAX_RETRIES) {
      await db.syncQueue.update(item.id, { status: "failed", retries: newRetries });
    } else {
      await db.syncQueue.update(item.id, { retries: newRetries });
    }
  }
}

function _invalidateTags(batch) {
  if (!_store) return;
  const tags = new Set();
  for (const item of batch) {
    switch (item.entityType) {
      case "record":
        tags.add("Record");
        break;
      case "comment":
        tags.add("Comment");
        break;
      case "trait_grade":
        tags.add("Trait");
        break;
      case "quiz_answer":
        tags.add("Quiz");
        break;
      default:
        break;
    }
  }
  if (tags.size > 0) {
    _store.dispatch(
      apiSlice.util.invalidateTags([...tags])
    );
  }
}

export async function hasPendingOfflineReportQueue(state = _store?.getState?.()) {
  const ctx = getOfflineContext(state);
  if (!ctx.userId || !ctx.schoolId) return false;
  const items = await db.syncQueue.toArray().catch(() => []);
  return items.some((item) => item?.userId === ctx.userId && item?.schoolId === ctx.schoolId && item?.status === "pending");
}

export async function clearCurrentUserOfflineData(state = _store?.getState?.()) {
  const ctx = getOfflineContext(state);
  if (!ctx.userId || !ctx.schoolId) {
    clearStoredOfflineContext();
    return;
  }

  const clearTableByPredicate = async (table, predicate) => {
    await table.toCollection().filter(predicate).delete();
  };

  const match = (row) => row?.userId === ctx.userId && row?.schoolId === ctx.schoolId;

  await clearTableByPredicate(db.syncQueue, match);
  await clearTableByPredicate(db.students, match);
  await clearTableByPredicate(db.assessments, match);
  await clearTableByPredicate(db.records, match);
  await clearTableByPredicate(db.comments, match);
  await clearTableByPredicate(db.traitGrades, match);
  await clearTableByPredicate(db.traits, match);
  await clearTableByPredicate(db.grades, match);
  await clearTableByPredicate(db.attendance, match);
  await clearTableByPredicate(db.preloadStatus, match);
  clearStoredOfflineContext();
}

export async function attemptOfflineAwareLogout({ forceDiscard = false } = {}) {
  const state = _store?.getState?.();
  if (forceDiscard) {
    setLogoutBypass(true);
    await clearCurrentUserOfflineData(state);
    return { allowed: true };
  }
  const blocked = await hasPendingOfflineReportQueue(state);
  if (blocked && !navigator.onLine && !getLogoutBypass()) {
    return { allowed: false, reason: "pending_offline_changes" };
  }
  await clearCurrentUserOfflineData(state);
  setLogoutBypass(false);
  return { allowed: true };
}

// ---------------------------------------------------------------------------
// Register global sync triggers (called once at app startup)
// ---------------------------------------------------------------------------

let _triggersRegistered = false;

export function registerSyncTriggers() {
  if (_triggersRegistered) return;
  _triggersRegistered = true;

  // Sync on reconnect
  window.addEventListener("online", () => {
    runSync();
  });

  // Sync when tab becomes visible while online
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && navigator.onLine) {
      runSync();
    }
  });
}
